/**
 * @module SettingsCoachVoice
 * @description Coach Yuri voice preference selector (masculine/feminine).
 */
import React, { memo } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Mic } from 'lucide-react';
import { toast } from 'sonner';

const SettingsCoachVoice = memo(function SettingsCoachVoice() {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();

  const voices = [
    { id: 'masculine', label_fr: 'Energique (Masculin)', label_en: 'Energetic (Male)', label_es: 'Energico (Masculino)', sub_fr: 'Par defaut', sub_en: 'Default', sub_es: 'Por defecto', testid: 'voice-masculine-btn' },
    { id: 'feminine', label_fr: 'Dynamique (Feminin)', label_en: 'Dynamic (Female)', label_es: 'Dinamica (Femenino)', sub_fr: 'Alternative', sub_en: 'Alternative', sub_es: 'Alternativa', testid: 'voice-feminine-btn' },
  ];

  const handleVoiceChange = async (voiceId) => {
    try {
      await api.put('/voice/preference', { voice_preference: voiceId });
      updateUser({ ...user, voice_preference: voiceId });
      const successMsg = {
        fr: `Voix ${voiceId === 'masculine' ? 'masculine' : 'feminine'} activee!`,
        en: `${voiceId} voice activated!`,
        es: `Voz ${voiceId === 'masculine' ? 'masculina' : 'femenina'} activada!`,
      }[language] || `${voiceId} voice activated!`;
      toast.success(successMsg);
    } catch {
      const errMsg = { fr: 'Erreur', en: 'Error', es: 'Error' }[language] || 'Error';
      toast.error(errMsg);
    }
  };

  const labelFor = (v) => language === 'es' ? v.label_es : (language === 'fr' ? v.label_fr : v.label_en);
  const subFor = (v) => language === 'es' ? v.sub_es : (language === 'fr' ? v.sub_fr : v.sub_en);

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          <Mic className="w-5 h-5 text-orange-400" />
          {language === 'es' ? 'Voz del Coach' : (language === 'fr' ? 'Voix du Coach' : 'Coach Voice')}
        </CardTitle>
        <CardDescription>
          {language === 'es'
            ? 'La personalidad y la filosofia YUMIX permanecen identicas, solo cambia el timbre de voz.'
            : language === 'fr'
              ? 'La personnalite et la philosophie YUMIX restent identiques, seul le timbre de voix change.'
              : 'The personality and YUMIX philosophy remain identical, only the voice changes.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {voices.map(v => (
            <button
              key={v.id}
              onClick={() => handleVoiceChange(v.id)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                (user?.voice_preference || 'masculine') === v.id
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-border hover:border-orange-500/50'
              }`}
              data-testid={v.testid}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">🎙️</span>
                <div>
                  <p className="font-semibold">{labelFor(v)}</p>
                  <p className="text-xs text-muted-foreground">{subFor(v)}</p>
                </div>
                {(user?.voice_preference || 'masculine') === v.id && (
                  <span className="ml-auto text-orange-500">✓</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
});

export default SettingsCoachVoice;
